import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { ResidentListHeader } from '../../../components/residentListHeader';
import api from '../../../api';
import { ListItem } from './listItem';
import { selfDataSelector } from '../../../redux/config/selfData';
import { filterConditionSelector } from '../../../redux/config/residentListSettings';
import { useLoading } from '../../../contexts/LoadingContext';
import { IGetResidentActiveRes } from '@/types/api/resident/getResidentActive';
import { IGetNursingHomeAdviceRes } from '@/types/api/advice/getNursingHomeAdvice';

const ListContainer = css`
  border-radius: 8px;
  overflow: hidden;
`;

const Container = css`
  padding: 0 16px 16px;
`;

const EmptyResident = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 16px;
`;

export const Advice: React.VFC = () => {
  const createFilterType = (condition: 'all' | 'deviceAssigned' | 'deviceAttached' | 'unit') => {
    if (['deviceAssigned', 'deviceAttached'].includes(condition)) {
      return condition as 'deviceAssigned' | 'deviceAttached';
    } else return null;
  };

  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const { selfUser } = useSelector(selfDataSelector);
  const { conditions, unitId } = useSelector(filterConditionSelector);
  const { showLoading, hideLoading } = useLoading();
  const { t } = useTranslation();

  const [residentAdviceList, setResidentAdviceList] = React.useState<IGetNursingHomeAdviceRes>([]);

  const handleUpdate = React.useCallback(() => {
    fetchAdvice(unitId, createFilterType(conditions));
  }, [unitId, conditions]);

  const handleChangeUnit = React.useCallback(
    (unitId: number | null, condition?: 'deviceAssigned' | 'deviceAttached') => {
      fetchAdvice(unitId, condition);
    },
    []
  );

  const fetchAdvice = React.useCallback(
    async (unitId?: number | null, condition?: 'deviceAssigned' | 'deviceAttached' | null) => {
      showLoading();
      const params = { nursingHomeId, limit: 50, sort: 'desc' };
      const adviceList = await api.get('/nursing-homes/:nursingHomeId/advices', {
        params: {
          ...params,
          ...(selfUser.hasUnitPerm ? { nursingHomeUnitId: selfUser.nursingHomeUnitId } : {}),
          ...(unitId ? { nursingHomeUnitId: unitId } : {}),
        },
      });

      const residents: IGetResidentActiveRes[] = await api.get('/nursing-homes/:nursingHomeId/residents/active', {
        params: { nursingHomeId },
      });

      if (!adviceList || !residents) return;

      const filteredResidents = (() => {
        if (condition === 'deviceAssigned') {
          return residents.filter((resident) => resident.serialNumber !== null || resident.gasSerialNumber !== null);
        } else if (condition === 'deviceAttached') {
          return residents.filter((resident) => resident.urinePercentage !== null || resident.gasPercentage !== null);
        } else {
          return residents;
        }
      })();
      const residentIds = filteredResidents.map((resident) => resident.id);
      const filteredAdvice = adviceList
        .filter((advice) => residentIds.includes(advice.residentId))
        .sort((a, b) => {
          if (a.timestamp < b.timestamp) return 1;
          if (a.timestamp > b.timestamp) return -1;
          return 0;
        });
        
      setResidentAdviceList(filteredAdvice);
      hideLoading();
    },
    [selfUser, residentAdviceList]
  );

  React.useEffect(() => {
    fetchAdvice(unitId, createFilterType(conditions));
  }, []);

  return (
    <div css={Container}>
      <ResidentListHeader
        title={t('advice', 'アドバイス')}
        conditions={conditions}
        unitId={unitId}
        onUpdate={handleUpdate}
        onChangeListSettings={handleChangeUnit}
      />
      {!residentAdviceList.length ? (
        <div css={EmptyResident}>{t('noAdviceForNow', 'アドバイスはありません')}</div>
      ) : (
        <div css={ListContainer}>
          {residentAdviceList.map((advice) => (
            <ListItem key={advice.id} advice={advice} />
          ))}
        </div>
      )}
    </div>
  );
};
